import { Plus, X } from "lucide-react";
import { SmartSelect } from "../../components/SmartInputs";
import { BouncyAccordion } from "../../components/beui/BouncyAccordion";
import { Card, CardContent, CardHeader } from "../../components/beui/SurfaceCard";
import { Checkbox } from "../../components/beui/Checkbox";
import { Button } from "../../components/beui/Button";
import { Input } from "../../components/beui/Input";
import { useI18n } from "../../lib/i18n";
import { ProjectRequirementKind, ProjectRequirementEntry, newProjectRequirementEntry, PROJECT_LICENSE_CHOICES, PROJECT_REQUIREMENT_SUGGESTIONS } from "./model";

export function ProjectRequirementsEditor({ requirements, licenses, selectedLicenses, onSelectedLicensesChange, onChange }: {
  requirements: ProjectRequirementEntry[];
  licenses: ProjectRequirementEntry[];
  selectedLicenses: string[];
  onSelectedLicensesChange: (licenses: string[]) => void;
  onChange: (kind: ProjectRequirementKind, entries: ProjectRequirementEntry[]) => void;
}) {
  const { t: translate } = useI18n();
  const groups: { kind: ProjectRequirementKind; label: string; entries: ProjectRequirementEntry[] }[] = [
    { kind: "requirements", label: translate("posting.requirements", "Requirements"), entries: requirements },
    { kind: "licenses", label: "Licenses & registrations", entries: licenses },
  ];
  return <Card>
    <CardHeader><div className="flex items-center justify-between gap-3"><h2 className="text-lg font-bold text-navy">Bidder requirements</h2><span className="text-xs text-mut">Optional</span></div></CardHeader>
    <CardContent>
      <BouncyAccordion items={groups.map(({ kind, label, entries }) => {
        const count = entries.filter(entry => entry.value.trim()).length + (kind === "licenses" ? selectedLicenses.length : 0);
        return {
          id: kind,
          title: <span className="flex items-center gap-3">{label}{count > 0 && <span className="text-xs font-normal text-mut">{count} added</span>}</span>,
          description: <div className="space-y-4">
            {kind === "licenses" && <div role="group" aria-label="Required licenses and registrations" className="grid gap-3 sm:grid-cols-2">{PROJECT_LICENSE_CHOICES.map(choice => <Card key={choice} interactive selected={selectedLicenses.includes(choice)} size="compact" className="rounded-xl py-0 focus-within:ring-2 focus-within:ring-navy-500 focus-within:ring-offset-2"><Checkbox className="min-h-14 w-full px-4 py-3" label={choice} checked={selectedLicenses.includes(choice)} onCheckedChange={checked => onSelectedLicensesChange(checked ? [...selectedLicenses, choice] : selectedLicenses.filter(value => value !== choice))} /></Card>)}</div>}
            {kind === "licenses" && <p className="label text-navy">Other license or registration</p>}
            <div className="space-y-3">{entries.map((entry, index) => <ProjectRequirementRow key={kind + index} entry={entry} label={kind === "licenses" ? "Other license or registration" : label} groupKind={kind} index={index} canRemove={entries.length > 1} onChange={next => onChange(kind, entries.map((current, i) => i === index ? next : current))} onRemove={() => onChange(kind, entries.filter((_, i) => i !== index))} />)}</div>
            <Button variant="outline" size="sm" className="h-11" disabled={entries.length >= 12} onClick={() => onChange(kind, [...entries, newProjectRequirementEntry()])}><Plus className="h-3.5 w-3.5" aria-hidden="true" />{kind === "requirements" ? "Add requirement" : "Add license"}</Button>
          </div>,
        };
      })} classNames={{ item: "border border-line bg-white", trigger: "min-h-14 px-4", title: "whitespace-normal", description: "text-sm" }} />
    </CardContent>
  </Card>;
}

export function ProjectRequirementRow({
  entry,
  label,
  groupKind,
  index,
  canRemove,
  onChange,
  onRemove,
}: {
  entry: ProjectRequirementEntry;
  label: string;
  groupKind: ProjectRequirementKind;
  index: number;
  canRemove: boolean;
  onChange: (entry: ProjectRequirementEntry) => void;
  onRemove: () => void;
}) {
  const suggestions = groupKind === "requirements" ? PROJECT_REQUIREMENT_SUGGESTIONS[(entry.type || "").trim().toLowerCase()] || [] : [];
  return (
    <div className="rounded-lg border border-line bg-navy-50/30 p-2.5">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 sm:flex">
        {groupKind === "requirements" && <div className="col-span-2 min-w-0 sm:w-40 sm:shrink-0"><label className="sr-only" htmlFor={`project-requirement-type-${index}`}>Requirement type {index + 1}</label><SmartSelect id={`project-requirement-type-${index}`} value={entry.type || ""} options={["Experience", "Portfolio", "Location", "Documents"]} maxLength={60} onChange={type => onChange({ ...entry, type, value: suggestions.includes(entry.value) ? "" : entry.value })} placeholder="Type" /></div>}
        {suggestions.length > 0
          ? <div className="min-w-0 flex-1"><label className="sr-only" htmlFor={`project-${groupKind}-${index}`}>{label} {index + 1}</label><SmartSelect id={`project-${groupKind}-${index}`} value={entry.value} onChange={(value) => onChange({ ...entry, value })} options={suggestions} maxLength={200} placeholder="e.g. 3 years of renovation experience" /></div>
          : <Input aria-label={label + " " + (index + 1)} className="min-h-11 flex-1 min-w-0" value={entry.value} maxLength={200} onChange={value => onChange({ ...entry, value })} placeholder={groupKind === "licenses" ? "e.g. Valid PCAB license, when applicable" : "e.g. Include labor, materials and cleanup"} />}
        <Button
          variant="ghost" size="icon"
          className="h-11 w-11 shrink-0 text-brand-red disabled:opacity-40"
          aria-label={`Remove ${label} ${index + 1}`}
          disabled={!canRemove}
          onClick={onRemove}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
